import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { NOSPACE_REGEX } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { notificationManagementApi } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import { MESSAGE } from "../../utils/validationMessage";

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Please enter at least 2 characters")
    .max(100, "Title must not be more than 100 characters."),
  // NOSPACE_REGEX is single-line only, so the multi-line message just has to
  // hold something other than whitespace.
  content: Yup.string()
    .required("Message is required")
    .test("not-blank", "Message is required", (value) =>
      Boolean(value && value.trim())
    )
    .max(1000, "Message must not be more than 1000 characters."),
  status: Yup.number()
    .typeError("Status is required")
    .required("Status is required")
    .oneOf([0, 1], "Status is required"),
});

/**
 * Preview only - the server is what actually generates and stores these.
 * Mirrors slugGenrator / generateNotificationTypeFromTitle in the backend
 * (squadduel-backend/src/utils/functions.js).
 */
const previewSlug = (title) =>
  title
    .trim()
    .replace(/[`~!@#|$%^&*()+=,./?><'":;_]/g, "")
    .replace(/ /g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const previewNotificationType = (title) =>
  title
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/** Pulls the {placeholder} tokens out of the message so the admin can see them. */
const extractPlaceholders = (content) => {
  const matches = String(content || "").match(/\{[a-zA-Z0-9_]+\}/g);
  return matches ? [...new Set(matches)] : [];
};

export default function AddEditNotificationTemplate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(Boolean(id));
  // slug/notificationType are generated once by the server and never change,
  // so in edit mode we only display what came back from the API.
  const [savedKeys, setSavedKeys] = useState({ slug: "", notificationType: "" });

  const formik = useFormik({
    initialValues: {
      title: "",
      content: "",
      status: 1,
    },
    validationSchema,

    onSubmit: (values, { setSubmitting }) => {
      const payload = {
        title: values.title.trim(),
        content: values.content.trim(),
        status: values.status,
      };

      const apiCall = id
        ? notificationManagementApi.editNotification({ id, ...payload })
        : notificationManagementApi.addNotification(payload);

      apiCall
        .then((res) => {
          toast.success(res?.data?.message);
          navigate("/notification-management");
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setValues } =
    formik;

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    notificationManagementApi
      .getByIdNotification({ id })
      .then((res) => {
        const data = res?.data?.data;
        setValues({
          title: data?.title ?? "",
          content: data?.content ?? "",
          status: data?.status ?? 1,
        });
        setSavedKeys({
          slug: data?.slug ?? "",
          notificationType: data?.notificationType ?? "",
        });
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const slug = id ? savedKeys.slug : previewSlug(formik.values.title);
  const notificationType = id
    ? savedKeys.notificationType
    : previewNotificationType(formik.values.title);

  const placeholders = useMemo(
    () => extractPlaceholders(formik.values.content),
    [formik.values.content]
  );

  return (
    <Card>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Notification Template</title>
      </Helmet>
      <CardContent>
        {loading ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <Typography variant="h4" sx={{ mb: 2 }} align="start">
              {id ? "Edit" : "Add"} Notification Template
            </Typography>
            <Form
              autoComplete="off"
              noValidate
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.name !== "content") {
                  e.preventDefault();
                }
              }}
            >
              <Stack spacing={3}>
                <TextField
                  name="title"
                  label="Title"
                  {...getFieldProps("title")}
                  error={Boolean(touched.title && errors.title)}
                  helperText={
                    (touched.title && errors.title) ||
                    "Shown as the notification heading."
                  }
                />

                <TextField
                  name="notificationType"
                  label="Notification Type"
                  value={notificationType}
                  disabled
                  helperText={
                    id
                      ? "Generated when the template was created. It cannot be changed."
                      : "Generated automatically from the title."
                  }
                />

                <TextField
                  name="slug"
                  label="Slug"
                  value={slug}
                  disabled
                  helperText={
                    id
                      ? "The app sends this notification by its slug, so it stays fixed."
                      : "Generated automatically from the title and fixed after saving."
                  }
                />

                <TextField
                  name="content"
                  label="Message"
                  multiline
                  minRows={4}
                  {...getFieldProps("content")}
                  error={Boolean(touched.content && errors.content)}
                  helperText={
                    (touched.content && errors.content) ||
                    "Use {name} for values the app fills in, e.g. {amount}."
                  }
                />

                <Alert severity="info">
                  <AlertTitle>Placeholders</AlertTitle>
                  <Typography variant="body2">
                    Anything you write inside curly braces, like{" "}
                    <strong>{"{amount}"}</strong> or{" "}
                    <strong>{"{currency}"}</strong>, is replaced with a real
                    value when the app sends this notification. A placeholder the
                    app does not send is replaced with empty text.
                  </Typography>
                  {placeholders.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Found in this message:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {placeholders.map((placeholder) => (
                          <Chip
                            key={placeholder}
                            label={placeholder}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Alert>

                <Grid item xs={12}>
                  <InputLabel id="status-label" sx={{ mb: 1 }}>
                    Status
                  </InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    value={formik.values.status}
                    onChange={(e) =>
                      formik.setFieldValue("status", e.target.value)
                    }
                    onBlur={() => formik.setFieldTouched("status", true)}
                    fullWidth
                    error={Boolean(
                      formik.touched.status && formik.errors.status
                    )}
                    displayEmpty
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                  {formik.touched.status && formik.errors.status ? (
                    <FormHelperText error>
                      {formik.errors.status}
                    </FormHelperText>
                  ) : (
                    <FormHelperText>
                      Only active templates are used when a notification is sent.
                    </FormHelperText>
                  )}
                </Grid>
              </Stack>
              <Stack
                spacing={2}
                mt={3}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  size="large"
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ my: 2 }}
                >
                  {id ? "Update" : "Add"}
                </Button>
                <Button
                  size="large"
                  variant="contained"
                  onClick={() => {
                    if (!isSubmitting) navigate("/notification-management");
                  }}
                  sx={{ my: 2, ml: 4 }}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          </FormikProvider>
        )}
      </CardContent>
    </Card>
  );
}
